import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from 'src/app/core/services/storage/storage.service';
import { CookieSettingsService } from '../../services/cookie-settings.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-cookie-consent',
  templateUrl: './cookie-consent.component.html',
  styleUrls: ['./cookie-consent.component.css']
})
export class CookieConsentComponent implements OnInit, OnDestroy {
  cookieAccepted = true;
  currentLanguage = 'EN';
  showCustomize = false;

  // Individual consent preferences
  requiredCookies = true; // Always true, cannot be disabled
  analyticalCookies = false;
  socialCookies = false;
  marketingCookies = false;

  private settingsSubscription: Subscription;

  constructor(
    private translate: TranslateService,
    private storageService: StorageService,
    private cookieSettingsService: CookieSettingsService
  ) {
    this.currentLanguage = this.translate.currentLang;
    this.translate.onLangChange.subscribe((language) => {
      this.currentLanguage = language.lang;
    });
  }

  ngOnInit(): void {
    const cookieInfo = this.storageService.getCookie('tresInfo');
    if (cookieInfo.length) {
      this.cookieAccepted = true;
      // Load existing preferences
      try {
        const preferences = JSON.parse(cookieInfo);
        this.analyticalCookies = preferences.analyticalCookies || false;
        this.socialCookies = preferences.socialCookies || false;
        this.marketingCookies = preferences.marketingCookies || false;
      } catch (e) {
        // If parsing fails, use defaults
      }
    } else {
      this.cookieAccepted = false;
    }

    // Listen for settings reopening from floating button
    this.settingsSubscription = this.cookieSettingsService.showSettings$.subscribe(show => {
      if (show) {
        this.cookieAccepted = false; // Reopen the modal
        this.showCustomize = false;  // Reset to main view
      }
    });
  }

  ngOnDestroy(): void {
    if (this.settingsSubscription) {
      this.settingsSubscription.unsubscribe();
    }
  }

  acceptAllCookies(): void {
    const data = {
      requiredCookies: true,
      analyticalCookies: true,
      socialCookies: true,
      marketingCookies: true,
      consentTimestamp: new Date().toISOString(),
      consentVersion: '1.0'
    };
    this.storageService.setCookie('tresInfo', data);
    this.cookieAccepted = true;
    this.cookieSettingsService.closeSettings();
    this.loadTrackingScripts(data);
  }

  rejectAllCookies(): void {
    // Only set required cookies, reject all optional ones
    const data = {
      requiredCookies: true,
      analyticalCookies: false,
      socialCookies: false,
      marketingCookies: false,
      consentTimestamp: new Date().toISOString(),
      consentVersion: '1.0'
    };
    this.storageService.setCookie('tresInfo', data);
    this.cookieAccepted = true;
    this.cookieSettingsService.closeSettings();
    // Do not load any tracking scripts
  }

  showCustomizeOptions(): void {
    this.showCustomize = true;
  }

  saveCustomPreferences(): void {
    const data = {
      requiredCookies: true, // Always required
      analyticalCookies: this.analyticalCookies,
      socialCookies: this.socialCookies,
      marketingCookies: this.marketingCookies,
      consentTimestamp: new Date().toISOString(),
      consentVersion: '1.0'
    };
    this.storageService.setCookie('tresInfo', data);
    this.cookieAccepted = true;
    this.cookieSettingsService.closeSettings();
    this.loadTrackingScripts(data);
  }

  private loadTrackingScripts(consent: any): void {
    // Load Google Analytics only if analytical cookies are accepted
    if (consent.analyticalCookies) {
      this.loadGoogleAnalytics();
      this.loadGoogleTagManager();
      this.loadMicrosoftClarity();
    }

    // Load marketing scripts only if marketing cookies are accepted
    if (consent.marketingCookies) {
      this.loadGoogleAdsense();
    }
  }

  private loadGoogleAnalytics(): void {
    if (!(window as any).gtag) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-MLWQ10TK07';
      document.head.appendChild(script);

      script.onload = () => {
        (window as any).dataLayer = (window as any).dataLayer || [];
        function gtag(...args: any[]) { (window as any).dataLayer.push(args); }
        (window as any).gtag = gtag;
        gtag('js', new Date());
        gtag('config', 'G-MLWQ10TK07');
      };
    }
  }

  private loadGoogleTagManager(): void {
    if (!(window as any).google_tag_manager) {
      const script = document.createElement('script');
      script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-NB6S5ZKL');`;
      document.head.appendChild(script);
    }
  }

  private loadMicrosoftClarity(): void {
    if (!(window as any).clarity) {
      const script = document.createElement('script');
      script.innerHTML = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window,document,"clarity","script","siy7pcy10a");`;
      document.head.appendChild(script);
    }
  }

  private loadGoogleAdsense(): void {
    if (!document.querySelector('script[src*="adsbygoogle"]')) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1115035479249914';
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }
  }
}
